import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Plus, Search, Ticket, Calendar, Users, CheckCircle, Clock, 
  FileText, Download, Send, Printer, RefreshCw, MoreVertical,
  Eye, Play, Square, Trash2, Crown, UserCheck, AlertCircle, Share2,
  CreditCard, DollarSign, ChevronLeft, ArrowLeft
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';

const TOKEN_TYPES = [
  { value: 'wajebaat', label: 'Wajebaat Takhmeen' },
  { value: 'fmb', label: 'FMB Takhmeen' },
  { value: 'payment', label: 'Payment Token' }
];

export default function TokenEventDetailPage() {
  const { api, user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { eventId } = useParams();
  
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('progress');
  const [dashboard, setDashboard] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [tokensLoading, setTokensLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [filters, setFilters] = useState({
    status: '',
    token_type: '',
    category: '',
    mohalla: '',
    search: ''
  });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedTokens, setSelectedTokens] = useState([]);
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  
  // Auto-refresh state - DISABLED by default
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  // Bluetooth Printer State for bulk print
  const [bluetoothDevice, setBluetoothDevice] = useState(null);
  const [bluetoothConnecting, setBluetoothConnecting] = useState(false);
  const [bluetoothPrinting, setBluetoothPrinting] = useState(false);
  const [printerCharacteristic, setPrinterCharacteristic] = useState(null);

  // Check if Web Bluetooth is available
  const isBluetoothAvailable = typeof navigator !== 'undefined' && navigator.bluetooth;

  // Fetch event details
  const fetchEvent = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/token-events/${eventId}`);
      setEvent(response.data);
    } catch (error) {
      toast.error('Failed to load event details');
      navigate('/token-events');
    } finally {
      setLoading(false);
    }
  }, [api, eventId, navigate]);

  useEffect(() => {
    fetchEvent();
  }, [fetchEvent]);

  // Connect to Bluetooth Printer
  const connectBluetoothPrinter = async () => {
    if (!isBluetoothAvailable) {
      toast.error('Bluetooth not available. Use Chrome on Android.');
      return false;
    }

    try {
      setBluetoothConnecting(true);
      
      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: [
          '000018f0-0000-1000-8000-00805f9b34fb',
          '49535343-fe7d-4ae5-8fa9-9fafd205e455',
          'e7810a71-73ae-499d-8c15-faa9aef0c3f2',
          '0000ff00-0000-1000-8000-00805f9b34fb',
          '0000ffe0-0000-1000-8000-00805f9b34fb',
          '0000fee7-0000-1000-8000-00805f9b34fb',
          '00001101-0000-1000-8000-00805f9b34fb',
          '49535343-8841-43f4-a8d4-ecbe34729bb3',
          '49535343-1e4d-4bd9-ba61-23c647249616',
          '0000fff0-0000-1000-8000-00805f9b34fb',
        ]
      });

      setBluetoothDevice(device);
      toast.info(`Connecting to ${device.name || 'Printer'}...`);
      
      let server;
      try {
        server = await device.gatt.connect();
      } catch (gattError) {
        console.error('GATT connect error:', gattError);
        toast.error('Cannot connect. Your printer may use Bluetooth Classic (not BLE). Try browser print instead.');
        setBluetoothConnecting(false);
        return false;
      }
      
      const characteristic = await findPrinterCharacteristic(server);
      
      if (characteristic) {
        setPrinterCharacteristic(characteristic);
        toast.success('Printer connected!');
        
        device.addEventListener('gattserverdisconnected', () => {
          setBluetoothDevice(null);
          setPrinterCharacteristic(null);
          toast.info('Printer disconnected');
        });
        
        return true;
      } else {
        toast.error('Printer found but not BLE compatible. Use browser print instead.');
        return false;
      }
    } catch (error) {
      console.error('Bluetooth error:', error);
      if (error.name === 'NotFoundError') {
        toast.info('No printer selected');
      } else {
        toast.error('Connection failed. Your printer may not support BLE.');
      }
      return false;
    } finally {
      setBluetoothConnecting(false);
    }
  };

  // Find writable characteristic for printing
  const findPrinterCharacteristic = async (server) => {
    const serviceUUIDs = [
      '000018f0-0000-1000-8000-00805f9b34fb',
      '49535343-fe7d-4ae5-8fa9-9fafd205e455',
      'e7810a71-73ae-499d-8c15-faa9aef0c3f2',
      '0000ff00-0000-1000-8000-00805f9b34fb',
      '0000ffe0-0000-1000-8000-00805f9b34fb',
      '0000fff0-0000-1000-8000-00805f9b34fb',
      '0000fee7-0000-1000-8000-00805f9b34fb',
    ];
    
    for (const serviceUUID of serviceUUIDs) {
      try {
        const service = await server.getPrimaryService(serviceUUID);
        const characteristics = await service.getCharacteristics();
        
        for (const char of characteristics) {
          const props = char.properties;
          if (props.write || props.writeWithoutResponse) {
            return char;
          }
        }
      } catch (e) {
        continue;
      }
    }
    
    try {
      const services = await server.getPrimaryServices();
      for (const service of services) {
        try {
          const characteristics = await service.getCharacteristics();
          for (const char of characteristics) {
            if (char.properties.write || char.properties.writeWithoutResponse) {
              return char;
            }
          }
        } catch (e) {
          continue;
        }
      }
    } catch (e) {
      console.log('Service enumeration failed');
    }
    
    return null;
  };

  // Generate ESC/POS commands for bulk token list
  const generateBulkTokenESCPOS = (tokensList) => {
    if (!event) return new Uint8Array([]);
    
    const encoder = new TextEncoder();
    const commands = [];
    
    const ESC = 0x1B;
    const GS = 0x1D;
    const LF = 0x0A;
    
    // Initialize printer
    commands.push(ESC, 0x40);
    
    // Header - Event Name
    commands.push(ESC, 0x61, 0x01); // Center
    commands.push(ESC, 0x45, 0x01); // Bold
    commands.push(GS, 0x21, 0x01); // Double height
    commands.push(...encoder.encode(event.name.substring(0, 24)));
    commands.push(LF);
    
    // Date
    commands.push(GS, 0x21, 0x00);
    commands.push(ESC, 0x45, 0x00);
    const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    commands.push(...encoder.encode(today));
    commands.push(LF);
    
    // Divider
    commands.push(...encoder.encode('================================'));
    commands.push(LF);
    
    // Total count
    commands.push(...encoder.encode(`Total Tokens: ${tokensList.length}`));
    commands.push(LF);
    commands.push(...encoder.encode('T=Takhmeen | P=Payment'));
    commands.push(LF);
    commands.push(...encoder.encode('--------------------------------'));
    commands.push(LF);
    
    const typeMap = {"wajebaat": "W", "fmb": "F", "payment": "P"};
    
    // Each token
    tokensList.forEach((token, idx) => {
      commands.push(ESC, 0x61, 0x00); // Left align
      
      // Token # and Type
      commands.push(ESC, 0x45, 0x01); // Bold
      commands.push(...encoder.encode(`#${token.token_number} [${typeMap[token.token_type] || 'T'}]`));
      commands.push(ESC, 0x45, 0x00);
      commands.push(LF);
      
      // Name (truncate if needed)
      const name = (token.full_name || '-').substring(0, 28);
      commands.push(...encoder.encode(name));
      commands.push(LF);
      
      // ITS and Mohalla
      commands.push(...encoder.encode(`${token.its_id} | ${token.mohalla?.substring(0, 10) || ''}`));
      commands.push(LF);
      
      // Clear Takhmeen and Payment status
      const takhmeenStatus = token.takhmeen_done ? 'Y' : 'N';
      const paymentStatus = token.payment_done ? 'Y' : 'N';
      commands.push(...encoder.encode(`T:${takhmeenStatus} | P:${paymentStatus}`));
      commands.push(LF);
      
      // Separator between tokens
      if (idx < tokensList.length - 1) {
        commands.push(...encoder.encode('- - - - - - - - - - - - - - - -'));
        commands.push(LF);
      }
    });
    
    // Footer
    commands.push(...encoder.encode('================================'));
    commands.push(LF);
    commands.push(ESC, 0x61, 0x01); // Center
    commands.push(...encoder.encode('Printed: ' + new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })));
    commands.push(LF, LF, LF, LF); // Feed paper
    
    return new Uint8Array(commands);
  };

  // Print all tokens via Bluetooth
  const handleBluetoothPrintAll = async () => {
    if (tokens.length === 0) {
      toast.error('No tokens to print');
      return;
    }

    try {
      setBluetoothPrinting(true);
      
      let characteristic = printerCharacteristic;
      
      if (!characteristic || !bluetoothDevice?.gatt?.connected) {
        toast.info('Connecting to printer...');
        const connected = await connectBluetoothPrinter();
        if (!connected) {
          setBluetoothPrinting(false);
          return;
        }
        // Wait for state to update
        await new Promise(resolve => setTimeout(resolve, 500));
        characteristic = printerCharacteristic;
      }
      
      if (!characteristic) {
        toast.error('Printer not ready. Please try again.');
        setBluetoothPrinting(false);
        return;
      }
      
      toast.info(`Printing ${tokens.length} tokens...`);
      const printData = generateBulkTokenESCPOS(tokens);
      
      // Send in chunks (BLE has MTU limits)
      const chunkSize = 100;
      for (let i = 0; i < printData.length; i += chunkSize) {
        const chunk = printData.slice(i, i + chunkSize);
        try {
          if (characteristic.properties.writeWithoutResponse) {
            await characteristic.writeValueWithoutResponse(chunk);
          } else {
            await characteristic.writeValue(chunk);
          }
          await new Promise(resolve => setTimeout(resolve, 50));
        } catch (writeError) {
          console.error('Write error:', writeError);
          throw writeError;
        }
      }
      
      toast.success(`${tokens.length} tokens sent to printer!`);
    } catch (error) {
      console.error('Bluetooth print error:', error);
      toast.error('Print failed. Try browser print instead.');
    } finally {
      setBluetoothPrinting(false);
    }
  };

  // Debounce search filter
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search || '');
    }, 300);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Print Token List - Receipt style for mobile printers
  const handlePrintTokenList = () => {
    if (tokens.length === 0 || !event) {
      toast.error('No tokens to print');
      return;
    }

    const tokenTypeMap = { "wajebaat": "Waj", "fmb": "FMB", "payment": "Pay" };
    const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    
    // Generate receipt-style HTML for thermal/mobile printer (58mm width)
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Token List - ${event.name}</title>
        <style>
          @page {
            size: 58mm auto;
            margin: 2mm;
          }
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: 'Courier New', monospace;
            font-size: 10px;
            line-height: 1.3;
            width: 54mm;
            max-width: 54mm;
            padding: 2mm;
          }
          .header {
            text-align: center;
            font-weight: bold;
            font-size: 12px;
            border-bottom: 1px dashed #000;
            padding-bottom: 3px;
            margin-bottom: 5px;
          }
          .event-name {
            font-size: 11px;
            word-wrap: break-word;
          }
          .date-info {
            font-size: 9px;
            color: #333;
            margin-top: 2px;
          }
          .summary {
            text-align: center;
            font-size: 10px;
            margin: 4px 0;
            padding: 2px;
            background: #f0f0f0;
          }
          .divider {
            border-top: 1px dashed #000;
            margin: 4px 0;
          }
          .token-item {
            border-bottom: 1px dotted #ccc;
            padding: 3px 0;
            page-break-inside: avoid;
          }
          .token-row {
            display: flex;
            justify-content: space-between;
            font-size: 9px;
          }
          .token-num {
            font-weight: bold;
            font-size: 11px;
          }
          .token-type {
            font-size: 8px;
            padding: 1px 3px;
            background: #e0e0e0;
            border-radius: 2px;
          }
          .name {
            font-weight: bold;
            font-size: 10px;
            word-wrap: break-word;
            overflow-wrap: break-word;
          }
          .its {
            font-family: monospace;
            font-size: 9px;
          }
          .info {
            font-size: 8px;
            color: #555;
          }
          .status-badge {
            font-size: 8px;
            padding: 1px 3px;
            border-radius: 2px;
            margin-left: 2px;
          }
          .done { background: #d4edda; color: #155724; }
          .pending { background: #fff3cd; color: #856404; }
          .paid { background: #cce5ff; color: #004085; }
          .status-row {
            display: flex;
            gap: 4px;
            font-size: 8px;
            margin-top: 2px;
          }
          .footer {
            text-align: center;
            font-size: 8px;
            margin-top: 5px;
            border-top: 1px dashed #000;
            padding-top: 3px;
          }
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="event-name">${event.name}</div>
          <div class="date-info">Generated: ${today}</div>
        </div>
        
        <div class="summary">
          Total: ${tokens.length} tokens
        </div>
        
        <div class="divider"></div>
        
        ${tokens.map((token, idx) => `
          <div class="token-item">
            <div class="token-row">
              <span class="token-num">#${token.token_number}</span>
              <span class="token-type">${tokenTypeMap[token.token_type] || token.token_type}</span>
            </div>
            <div class="name">${token.full_name || '-'}</div>
            <div class="token-row">
              <span class="its">ITS: ${token.its_id}</span>
            </div>
            <div class="info">${token.mohalla || ''} ${token.sector ? '| S' + token.sector : ''}</div>
            <div class="status-row">
              <span class="status-badge ${token.takhmeen_done ? 'done' : 'pending'}">T: ${token.takhmeen_done ? '✓' : '✗'}</span>
              <span class="status-badge ${token.payment_done ? 'paid' : 'pending'}">P: ${token.payment_done ? '✓' : '✗'}</span>
            </div>
          </div>
        `).join('')}
        
        <div class="footer">
          T=Takhmeen | P=Payment<br/>
          Printed: ${new Date().toLocaleString('en-IN')}
        </div>
      </body>
      </html>
    `;

    // Open print dialog
    const printWindow = window.open('', '_blank', 'width=300,height=600');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
      };
    } else {
      toast.error('Please allow popups to print');
    }
  };

  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    if (!event) return;
    try {
      const response = await api.get(`/token-events/${event.id}/dashboard?date=${selectedDate}`);
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard');
    }
  }, [api, event, selectedDate]);

  // Fetch tokens - filtered by current/selected date
  const fetchTokens = async (statusFilter = '') => {
    if (!event) return;
    try {
      setTokensLoading(true);
      const queryFilters = {
        ...filters,
        search: debouncedSearch,
        // Only filter by date if NOT searching - search should look across all dates
        // But if user explicitly selected a different date, use that
        ...(debouncedSearch ? {} : { date: selectedDate })
      };
      if (statusFilter) {
        queryFilters.status = statusFilter;
      }
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '100',  // Show more tokens per page
        ...Object.fromEntries(Object.entries(queryFilters).filter(([_, v]) => v))
      });
      const response = await api.get(`/token-events/${event.id}/tokens?${params}`);
      setTokens(response.data.tokens || []);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      console.error('Failed to fetch tokens:', error);
      toast.error('Failed to fetch tokens');
    } finally {
      setTokensLoading(false);
    }
  };

  useEffect(() => {
    if (event) {
      fetchDashboard();
    }
  }, [fetchDashboard, event]);

  useEffect(() => {
    if (!event) return;
    if (activeTab === 'today') {
      // Fetch tokens for selected date only
      fetchTokens('');
    } else if (activeTab === 'all_tokens') {
      // Fetch all tokens (no date filter)
      fetchAllTokens();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, page, event?.id, filters.token_type, filters.status, debouncedSearch, selectedDate]);
  
  // Fetch ALL tokens (no date filter)
  const fetchAllTokens = async () => {
    if (!event) return;
    try {
      setTokensLoading(true);
      const queryFilters = {
        ...filters,
        search: debouncedSearch,
        // No date filter for Full Token List
      };
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '100',
        ...Object.fromEntries(Object.entries(queryFilters).filter(([_, v]) => v))
      });
      const response = await api.get(`/token-events/${event.id}/tokens?${params}`);
      setTokens(response.data.tokens || []);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      console.error('Failed to fetch all tokens:', error);
      toast.error('Failed to fetch tokens');
    } finally {
      setTokensLoading(false);
    }
  };

  // Auto-refresh every 10 seconds when enabled - SILENT refresh (no UI disruption)
  useEffect(() => {
    if (!autoRefresh || !event) return;
    
    const interval = setInterval(async () => {
      try {
        // Silently refresh dashboard data only (no loading states)
        const dashResponse = await api.get(`/token-events/${event.id}/dashboard?date=${selectedDate}`);
        if (dashResponse.data) {
          setDashboard(dashResponse.data);
        }
        setLastRefresh(new Date());
      } catch (error) {
        console.error('Auto-refresh failed:', error);
      }
    }, 10000);
    
    return () => clearInterval(interval);
  }, [autoRefresh, event, selectedDate, api]);

  // Update token status
  const handleUpdateStatus = async (tokenId, status) => {
    try {
      await api.put(`/tokens/${tokenId}/status`, { status });
      toast.success(`Token marked as ${status}`);
      if (activeTab === 'pending') {
        fetchTokens('pending');
      } else {
        fetchTokens('');
      }
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to update token status');
    }
  };

  // Bulk update status
  const handleBulkStatus = async (status) => {
    if (selectedTokens.length === 0) {
      toast.error('Please select tokens first');
      return;
    }
    try {
      await api.put('/tokens/bulk-status', { token_ids: selectedTokens, status });
      toast.success(`${selectedTokens.length} tokens marked as ${status}`);
      setSelectedTokens([]);
      fetchTokens();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to update tokens');
    }
  };

  // Export report - supports both main export and drilldown export
  const handleExport = async (format, drilldownMohalla = null, drilldownFilterType = null) => {
    if (!event) return;
    try {
      const params = new URLSearchParams({
        format,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      });
      
      // Add drilldown filters if provided (for mohalla-specific exports)
      if (drilldownMohalla) {
        params.set('mohalla', drilldownMohalla);
      }
      if (drilldownFilterType) {
        params.set('filter_type', drilldownFilterType);
      }
      
      const response = await api.get(`/token-events/${event.id}/export?${params}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Create descriptive filename based on filter
      let filename = `tokens_${event.name.replace(/\s+/g, '_')}`;
      if (drilldownMohalla) {
        filename += `_${drilldownMohalla.replace(/\s+/g, '_')}`;
      }
      if (drilldownFilterType) {
        filename += `_${drilldownFilterType}`;
      }
      filename += `_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${format.toUpperCase()} exported successfully`);
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  // Print report
  const handlePrintReport = async () => {
    if (!event) return;
    try {
      const params = new URLSearchParams({
        format: 'pdf',
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      });
      
      const response = await api.get(`/token-events/${event.id}/export?${params}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    } catch (error) {
      toast.error('Failed to generate print preview');
    }
  };

  // Delete Event Handler
  const handleDeleteEvent = async () => {
    if (!event) return;
    if (!window.confirm(`Are you sure you want to delete this event? This action cannot be undone.\n\nThis will permanently delete:\n• All related tokens\n• Dashboard data\n• Pending lists`)) {
      return;
    }
    
    try {
      await api.delete(`/token-events/${event.id}`);
      toast.success('Event deleted successfully');
      navigate('/token-events');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete event');
    }
  };

  // State for mohalla drilldown
  const [drilldownData, setDrilldownData] = useState(null);
  const [showDrilldown, setShowDrilldown] = useState(false);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  
  // Generate Token Dialog state
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);

  // Fetch mohalla drilldown
  const fetchMohallaDrilldown = async (mohalla, filterType) => {
    if (!event) return;
    setDrilldownLoading(true);
    try {
      const response = await api.get(`/token-events/${event.id}/mohalla-drilldown?mohalla=${encodeURIComponent(mohalla)}&filter_type=${filterType}`);
      setDrilldownData(response.data);
      setShowDrilldown(true);
    } catch (error) {
      toast.error('Failed to load details');
    } finally {
      setDrilldownLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-navy" />
      </div>
    );
  }

  // Event not found
  if (!event) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
        <p className="text-slate-500">Event not found</p>
        <Button onClick={() => navigate('/token-events')} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Events
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Back Button */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => navigate('/token-events')}
            data-testid="back-to-events-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div className="flex items-center gap-2 flex-wrap">
            <Ticket className="w-5 h-5 sm:w-6 sm:h-6 text-gold" />
            <h1 className="text-lg sm:text-xl font-bold text-navy break-words">{event.name}</h1>
            {event.is_completed ? (
              <Badge className="bg-slate-500 text-xs">Completed</Badge>
            ) : event.is_active ? (
              <Badge className="bg-emerald-500 text-xs">Active</Badge>
            ) : (
              <Badge className="bg-amber-500 text-xs">Inactive</Badge>
            )}
          </div>
        </div>
        {/* Delete Event Button */}
        {isAdmin && (
          <Button 
            variant="destructive" 
            size="sm" 
            onClick={handleDeleteEvent}
            data-testid="delete-event-btn"
            className="w-full sm:w-auto"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Event
          </Button>
        )}
      </div>

      {/* Date Selector and Auto-Refresh - Always visible */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-3 bg-slate-50 rounded-lg">
        <Label className="font-medium text-sm">View Date:</Label>
        <div className="flex items-center gap-2 flex-1">
          <Input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="flex-1 sm:w-[180px] sm:flex-none"
            data-testid="date-selector"
          />
          <Button variant="outline" size="sm" onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}>
            Today
          </Button>
        </div>
        {/* Auto-Refresh Toggle */}
        <div className="flex items-center gap-2 border-l pl-2 sm:pl-4">
          <Button
            variant={autoRefresh ? "default" : "outline"}
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={autoRefresh ? "bg-green-600 hover:bg-green-700" : ""}
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${autoRefresh ? 'animate-spin' : ''}`} />
            {autoRefresh ? 'Auto' : 'Manual'}
          </Button>
          <span className="text-xs text-slate-500 hidden sm:inline">
            {autoRefresh ? `Every 10s` : 'Click to enable'}
          </span>
        </div>
        {/* Generate Token Button - Quick access */}
        {!event.is_completed && (
          <Button 
            size="sm" 
            className="bg-emerald-600 hover:bg-emerald-700"
            onClick={() => setShowGenerateDialog(true)}
            data-testid="quick-generate-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Generate Token
          </Button>
        )}
      </div>

      {/* Mohalla Drilldown Dialog */}
      {showDrilldown && drilldownData && (
        <Dialog open={showDrilldown} onOpenChange={() => setShowDrilldown(false)}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-lg">
                {drilldownData.mohalla} - {
                  drilldownData.filter_type === 'takhmeen_done' ? 'Takhmeen Done' :
                  drilldownData.filter_type === 'takhmeen_pending' ? 'Pending Tokens' :
                  drilldownData.filter_type === 'payment_done' ? 'Payment Done' :
                  drilldownData.filter_type === 'pending_hof' ? 'Pending HOF' :
                  'Payment Pending'
                }
                <Badge className="ml-2">{drilldownData.total} {drilldownData.filter_type === 'pending_hof' ? 'HOFs' : 'tokens'}</Badge>
              </DialogTitle>
            </DialogHeader>
            
            {/* Export buttons */}
            <div className="flex gap-2 mb-4">
              <Button 
                onClick={() => handleExport('pdf', drilldownData.mohalla, drilldownData.filter_type)} 
                size="sm" 
                className="bg-red-600 hover:bg-red-700"
              >
                <Download className="w-4 h-4 mr-1" /> PDF
              </Button>
              {user?.role === 'super_admin' && (
                <Button 
                  onClick={() => handleExport('excel', drilldownData.mohalla, drilldownData.filter_type)} 
                  size="sm" 
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Download className="w-4 h-4 mr-1" /> Excel
                </Button>
              )}
            </div>

            {/* Drilldown Table - Different columns for Pending HOF */}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Sr</TableHead>
                  <TableHead>ITS</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Mohalla</TableHead>
                  <TableHead>Sector</TableHead>
                  {drilldownData.filter_type === 'pending_hof' ? (
                    <>
                      <TableHead>Last Token Date</TableHead>
                      <TableHead>Status</TableHead>
                    </>
                  ) : (
                    <>
                      <TableHead>Token Type</TableHead>
                      <TableHead>Token #</TableHead>
                      <TableHead>Date</TableHead>
                    </>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {drilldownData.tokens.map((token, idx) => (
                  <TableRow key={token.id || token.its_id}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell className="font-mono">{token.its_id}</TableCell>
                    <TableCell>{token.full_name}</TableCell>
                    <TableCell>{token.mohalla}</TableCell>
                    <TableCell>{token.sector || '-'}</TableCell>
                    {drilldownData.filter_type === 'pending_hof' ? (
                      <>
                        <TableCell>{token.last_token_date || 'No token'}</TableCell>
                        <TableCell>
                          <Badge className="bg-red-500">{token.status}</Badge>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>
                          <Badge variant="outline">
                            {{"wajebaat": "Wajebaat", "fmb": "FMB", "payment": "Payment"}[token.token_type] || token.token_type || '-'}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-bold">{token.token_number || '-'}</TableCell>
                        <TableCell>{token.generation_date || '-'}</TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </DialogContent>
        </Dialog>
      )}

      {/* Tabs - NEW 4-TAB STRUCTURE */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 w-full">
          <TabsTrigger value="progress" className="text-xs sm:text-sm px-1 sm:px-3">
            <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
            <span className="hidden sm:inline">Event Progress</span>
            <span className="sm:hidden">Progress</span>
          </TabsTrigger>
          <TabsTrigger value="today" className="text-xs sm:text-sm px-1 sm:px-3">
            <Calendar className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
            <span className="hidden sm:inline">Today's Tokens</span>
            <span className="sm:hidden">Today</span>
          </TabsTrigger>
          <TabsTrigger value="all_tokens" className="text-xs sm:text-sm px-1 sm:px-3">
            <Ticket className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
            <span className="hidden sm:inline">Full Token List</span>
            <span className="sm:hidden">All</span>
          </TabsTrigger>
          <TabsTrigger value="pending" className="text-xs sm:text-sm px-1 sm:px-3 relative">
            <Clock className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
            <span className="hidden sm:inline">Pending HOF</span>
            <span className="sm:hidden">HOF</span>
            {dashboard?.status_counts?.pending_hof > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                {dashboard?.status_counts?.pending_hof > 99 ? '99+' : dashboard?.status_counts?.pending_hof}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Tab 1: Event Progress (Official Counts) */}
        <TabsContent value="progress" className="mt-4">
          {dashboard && (
            <div className="space-y-4">
              {/* Official Status Counts - Based on event_member_status */}
              <div>
                <p className="text-sm font-medium text-slate-600 mb-2">Official Event Progress (from event_member_status):</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                  <Card className="bg-emerald-500 text-white">
                    <CardContent className="p-2 sm:p-3 text-center">
                      <p className="text-xl sm:text-2xl font-bold">{dashboard.status_counts?.takhmeen_done || 0}</p>
                      <p className="text-[10px] sm:text-xs opacity-80">Takhmeen Done</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-orange-500 text-white">
                    <CardContent className="p-2 sm:p-3 text-center">
                      <p className="text-xl sm:text-2xl font-bold">{dashboard.status_counts?.payment_pending || 0}</p>
                      <p className="text-[10px] sm:text-xs opacity-80">Payment Pending</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-purple-500 text-white">
                    <CardContent className="p-2 sm:p-3 text-center">
                      <p className="text-xl sm:text-2xl font-bold">{dashboard.status_counts?.payment_completed || 0}</p>
                      <p className="text-[10px] sm:text-xs opacity-80">Payment Completed</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-red-500 text-white">
                    <CardContent className="p-2 sm:p-3 text-center">
                      <p className="text-xl sm:text-2xl font-bold">{dashboard.status_counts?.pending_hof || 0}</p>
                      <p className="text-[10px] sm:text-xs opacity-80">Pending HOF</p>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Mohalla-wise Breakdown - CLICKABLE */}
              {dashboard.mohalla_breakdown && dashboard.mohalla_breakdown.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-600 mb-2">Mohalla-wise Breakdown (Click to view details):</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {dashboard.mohalla_breakdown.map((mohalla) => (
                      <Card key={mohalla._id} className="border-l-4 border-l-navy">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-bold text-navy">{mohalla._id}</CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0 space-y-2">
                          <div 
                            className="flex justify-between items-center p-2 bg-emerald-50 rounded cursor-pointer hover:bg-emerald-100"
                            onClick={() => fetchMohallaDrilldown(mohalla._id, 'takhmeen_done')}
                            data-testid={`mohalla-takhmeen-done-${mohalla._id}`}
                          >
                            <span className="text-sm text-emerald-700">Takhmeen Done</span>
                            <Badge className="bg-emerald-500">{mohalla.takhmeen_done || 0}</Badge>
                          </div>
                          <div 
                            className="flex justify-between items-center p-2 bg-orange-50 rounded cursor-pointer hover:bg-orange-100"
                            onClick={() => fetchMohallaDrilldown(mohalla._id, 'payment_pending')}
                            data-testid={`mohalla-payment-pending-${mohalla._id}`}
                          >
                            <span className="text-sm text-orange-700">Payment Pending</span>
                            <Badge className="bg-orange-500">{mohalla.payment_pending || 0}</Badge>
                          </div>
                          <div 
                            className="flex justify-between items-center p-2 bg-purple-50 rounded cursor-pointer hover:bg-purple-100"
                            onClick={() => fetchMohallaDrilldown(mohalla._id, 'payment_done')}
                            data-testid={`mohalla-payment-done-${mohalla._id}`}
                          >
                            <span className="text-sm text-purple-700">Payment Completed</span>
                            <Badge className="bg-purple-500">{mohalla.payment_completed || 0}</Badge>
                          </div>
                          <div 
                            className="flex justify-between items-center p-2 bg-red-50 rounded cursor-pointer hover:bg-red-100"
                            onClick={() => fetchMohallaDrilldown(mohalla._id, 'pending_hof')}
                            data-testid={`mohalla-pending-hof-${mohalla._id}`}
                          >
                            <span className="text-sm text-red-700">Pending HOF</span>
                            <Badge className="bg-red-500">{mohalla.pending_hof || 0}</Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Daily History */}
              {dashboard.history && dashboard.history.length > 0 && (
                <details className="bg-slate-50 rounded-lg p-3">
                  <summary className="cursor-pointer font-medium text-sm text-slate-700">View Daily History ({dashboard.history.length} days)</summary>
                  <div className="mt-3 overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Tokens</TableHead>
                          <TableHead>Takhmeen</TableHead>
                          <TableHead>Payment</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {dashboard.history.map((day) => (
                          <TableRow key={day._id} className={day._id === selectedDate ? 'bg-blue-50' : ''}>
                            <TableCell className="font-mono">{day._id}</TableCell>
                            <TableCell>{day.tokens_generated || 0}</TableCell>
                            <TableCell>{day.takhmeen_marked || 0}</TableCell>
                            <TableCell>{day.payment_marked || 0}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </details>
              )}

              {/* Export/Reports Section */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Export Reports</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <Select value={filters.completion_filter || 'all'} onValueChange={(v) => setFilters({...filters, completion_filter: v === 'all' ? '' : v})}>
                      <SelectTrigger className="h-9"><SelectValue placeholder="Filter" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="takhmeen_done">Takhmeen Done</SelectItem>
                        <SelectItem value="payment_pending">Payment Pending</SelectItem>
                        <SelectItem value="payment_done">Payment Done</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button onClick={() => handleExport('pdf')} className="bg-red-600 hover:bg-red-700 text-xs sm:text-sm" data-testid="progress-export-pdf-btn">
                      <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                      PDF
                    </Button>
                    {user?.role === 'super_admin' && (
                      <Button onClick={() => handleExport('excel')} className="bg-emerald-600 hover:bg-emerald-700 text-xs sm:text-sm" data-testid="progress-export-excel-btn">
                        <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                        Excel
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Tab 2: Today's Tokens (YYYY-MM-DD) - With Bluetooth Print */}
        <TabsContent value="today" className="mt-4">
          <div className="space-y-3">
            {/* Today's Token Stats - Token Types */}
            {dashboard && (
              <>
                <p className="text-sm font-medium text-slate-600">Tokens Generated ({selectedDate}):</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Card className="bg-blue-500 text-white">
                    <CardContent className="p-2 text-center">
                      <p className="text-lg font-bold">{dashboard.today?.total_tokens || 0}</p>
                      <p className="text-[10px] opacity-80">Total Today</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-indigo-400 text-white">
                    <CardContent className="p-2 text-center">
                      <p className="text-lg font-bold">{dashboard.today?.wajebaat_tokens || 0}</p>
                      <p className="text-[10px] opacity-80">Wajebaat</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-teal-400 text-white">
                    <CardContent className="p-2 text-center">
                      <p className="text-lg font-bold">{dashboard.today?.fmb_tokens || 0}</p>
                      <p className="text-[10px] opacity-80">FMB</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-violet-400 text-white">
                    <CardContent className="p-2 text-center">
                      <p className="text-lg font-bold">{dashboard.today?.payment_tokens || 0}</p>
                      <p className="text-[10px] opacity-80">Payment</p>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Today's Completion Status Counts */}
                <p className="text-sm font-medium text-slate-600 mt-3">Today's Completion Status:</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Card className="bg-emerald-500 text-white">
                    <CardContent className="p-2 text-center">
                      <p className="text-lg font-bold">{dashboard.today?.takhmeen_marked || 0}</p>
                      <p className="text-[10px] opacity-80">Takhmeen Done</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-orange-500 text-white">
                    <CardContent className="p-2 text-center">
                      <p className="text-lg font-bold">{(dashboard.today?.total_tokens || 0) - (dashboard.today?.takhmeen_marked || 0)}</p>
                      <p className="text-[10px] opacity-80">Takhmeen Pending</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-purple-500 text-white">
                    <CardContent className="p-2 text-center">
                      <p className="text-lg font-bold">{dashboard.today?.payment_marked || 0}</p>
                      <p className="text-[10px] opacity-80">Payment Done</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-amber-500 text-white">
                    <CardContent className="p-2 text-center">
                      <p className="text-lg font-bold">{Math.max(0, (dashboard.today?.takhmeen_marked || 0) - (dashboard.today?.payment_marked || 0))}</p>
                      <p className="text-[10px] opacity-80">Payment Pending</p>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
            
            <TokensListView
              tokens={tokens}
              loading={tokensLoading}
              filters={filters}
              setFilters={setFilters}
              page={page}
              setPage={setPage}
              totalPages={totalPages}
              selectedTokens={selectedTokens}
              setSelectedTokens={setSelectedTokens}
              onUpdateStatus={handleUpdateStatus}
              onBulkStatus={handleBulkStatus}
              onSendReminder={() => setShowReminderDialog(true)}
              onRefresh={() => fetchTokens('')}
              showStatusFilter={true}
              api={api}
              eventId={event.id}
              onBluetoothPrint={handleBluetoothPrintAll}
              bluetoothConnecting={bluetoothConnecting}
              bluetoothPrinting={bluetoothPrinting}
              printerConnected={!!printerCharacteristic}
              onConnectPrinter={connectBluetoothPrinter}
              selectedDate={selectedDate}
              showBluetoothPrint={true}
            />
          </div>
        </TabsContent>

        {/* Tab 3: Full Token List (All Dates) */}
        <TabsContent value="all_tokens" className="mt-4">
          <div className="space-y-3">
            {/* All-Time Token Stats */}
            {dashboard && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Card className="bg-navy text-white">
                  <CardContent className="p-2 text-center">
                    <p className="text-lg font-bold">{dashboard.full?.total_tokens || 0}</p>
                    <p className="text-[10px] opacity-80">Total Tokens</p>
                  </CardContent>
                </Card>
                <Card className="bg-indigo-600 text-white">
                  <CardContent className="p-2 text-center">
                    <p className="text-lg font-bold">{dashboard.full?.wajebaat_tokens || 0}</p>
                    <p className="text-[10px] opacity-80">Wajebaat</p>
                  </CardContent>
                </Card>
                <Card className="bg-teal-600 text-white">
                  <CardContent className="p-2 text-center">
                    <p className="text-lg font-bold">{dashboard.full?.fmb_tokens || 0}</p>
                    <p className="text-[10px] opacity-80">FMB</p>
                  </CardContent>
                </Card>
                <Card className="bg-violet-600 text-white">
                  <CardContent className="p-2 text-center">
                    <p className="text-lg font-bold">{dashboard.full?.payment_tokens || 0}</p>
                    <p className="text-[10px] opacity-80">Payment</p>
                  </CardContent>
                </Card>
              </div>
            )}
            
            <TokensListView
              tokens={tokens}
              loading={tokensLoading}
              filters={filters}
              setFilters={setFilters}
              page={page}
              setPage={setPage}
              totalPages={totalPages}
              selectedTokens={selectedTokens}
              setSelectedTokens={setSelectedTokens}
              onUpdateStatus={handleUpdateStatus}
              onBulkStatus={handleBulkStatus}
              onSendReminder={() => setShowReminderDialog(true)}
              onRefresh={() => fetchAllTokens()}
              showStatusFilter={true}
              api={api}
              eventId={event.id}
              onPrintTokenList={handlePrintTokenList}
              selectedDate={null}
              showBluetoothPrint={false}
            />
          </div>
        </TabsContent>

        {/* Tab 4: Pending HOF */}
        <TabsContent value="pending" className="mt-4">
          <PendingHofView
            eventId={event.id}
            api={api}
            onGenerateToken={(family) => {
              setShowGenerateDialog(true);
            }}
          />
        </TabsContent>
      </Tabs>

      {/* Generate Token Dialog */}
      {showGenerateDialog && (
        <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Ticket className="w-5 h-5 text-gold" />
                Generate Token
              </DialogTitle>
            </DialogHeader>
            {event.is_completed ? (
              <div className="text-center py-8 text-slate-500">
                <AlertCircle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
                <p>This event is completed. No new tokens can be generated.</p>
              </div>
            ) : (
              <TokenGenerationForm 
                eventId={event.id} 
                api={api} 
                onSuccess={() => { 
                  fetchDashboard(); 
                  setShowGenerateDialog(false);
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Reminder Dialog */}
      {showReminderDialog && selectedTokens.length > 0 && (
        <ReminderDialog 
          api={api} 
          tokenIds={selectedTokens} 
          onClose={() => setShowReminderDialog(false)}
          onSuccess={() => {
            setShowReminderDialog(false);
            setSelectedTokens([]);
          }}
        />
      )}
    </div>
  );
}

// Import the shared components from TokenEventsPage
// These are defined in the original file and will be imported

// Token Generation Form Component (inline for now)
function TokenGenerationForm({ eventId, api, onSuccess }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [tokenType, setTokenType] = useState('wajebaat');
  const [isVip, setIsVip] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedToken, setGeneratedToken] = useState(null);
  const [availableVip, setAvailableVip] = useState([]);
  const [memberStatus, setMemberStatus] = useState(null);
  const [memberStatusLoading, setMemberStatusLoading] = useState(false);

  // Search HOF
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearchLoading(true);
    try {
      const response = await api.get(`/token-events/search-hof?q=${encodeURIComponent(searchQuery)}`);
      setSearchResults(response.data.results || []);
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setSearchLoading(false);
    }
  };

  // Fetch available VIP numbers
  const fetchAvailableVip = async () => {
    try {
      const response = await api.get(`/token-events/${eventId}/available-vip?token_type=${tokenType}`);
      setAvailableVip(response.data.available_numbers || []);
    } catch (error) {
      console.error('Failed to fetch VIP numbers');
    }
  };

  // Fetch member status when selected
  const fetchMemberStatus = async (itsId) => {
    setMemberStatusLoading(true);
    try {
      const response = await api.get(`/token-events/${eventId}/member-status/${itsId}`);
      setMemberStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch member status');
      setMemberStatus(null);
    } finally {
      setMemberStatusLoading(false);
    }
  };

  useEffect(() => {
    if (eventId) {
      fetchAvailableVip();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, tokenType]);

  useEffect(() => {
    if (selectedMember) {
      fetchMemberStatus(selectedMember.its_id);
    } else {
      setMemberStatus(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMember]);

  // Generate token
  const handleGenerateToken = async () => {
    if (!selectedMember) {
      toast.error('Please select a member');
      return;
    }

    setGenerating(true);
    try {
      const response = await api.post(`/token-events/${eventId}/generate`, {
        its_id: selectedMember.its_id,
        token_type: tokenType,
        category: isVip ? 'vip' : 'general'
      });
      
      setGeneratedToken(response.data.token);
      toast.success(`Token #${response.data.token.token_number} generated!`);
      
      // Reset form
      setSearchQuery('');
      setSearchResults([]);
      setSelectedMember(null);
      setIsVip(false);
      setMemberStatus(null);
      
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate token');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Search Section */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Search HOF</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input
              placeholder="Search by ITS, Name or Mobile..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className="flex-1"
            />
            <Button onClick={handleSearch} disabled={searchLoading}>
              {searchLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>
          
          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="max-h-48 overflow-y-auto border rounded-lg">
              {searchResults.map((member) => (
                <div 
                  key={member.id}
                  className={`p-3 border-b cursor-pointer hover:bg-slate-50 ${selectedMember?.its_id === member.its_id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''}`}
                  onClick={() => setSelectedMember(member)}
                >
                  <div className="flex justify-between">
                    <span className="font-medium">{member.full_name}</span>
                    <Badge variant="outline">{member.its_id}</Badge>
                  </div>
                  <div className="text-sm text-slate-500">
                    {member.mohalla} {member.sector ? `| Sector ${member.sector}` : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Selected Member + Token Options */}
      {selectedMember && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Generate Token</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Member Info */}
            <div className="p-3 bg-slate-50 rounded-lg">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium">{selectedMember.full_name}</p>
                  <p className="text-sm text-slate-500">ITS: {selectedMember.its_id}</p>
                  <p className="text-sm text-slate-500">{selectedMember.mohalla} {selectedMember.sector ? `| Sector ${selectedMember.sector}` : ''}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedMember(null)}>
                  <Square className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Member Status */}
              {memberStatusLoading ? (
                <div className="mt-2 text-sm text-slate-500">Loading status...</div>
              ) : memberStatus && (
                <div className="mt-2 flex gap-2 flex-wrap">
                  {memberStatus.takhmeen_status === 'completed' && (
                    <Badge className="bg-emerald-500">Takhmeen Done</Badge>
                  )}
                  {memberStatus.payment_status === 'completed' && (
                    <Badge className="bg-purple-500">Payment Done</Badge>
                  )}
                  {memberStatus.payment_status === 'pending' && memberStatus.takhmeen_status === 'completed' && (
                    <Badge className="bg-orange-500">Payment Pending</Badge>
                  )}
                  {memberStatus.takhmeen_status !== 'completed' && (
                    <Badge className="bg-amber-500">Takhmeen Pending</Badge>
                  )}
                </div>
              )}
            </div>

            {/* Token Type */}
            <div>
              <Label className="text-sm font-medium">Token Type</Label>
              <Select value={tokenType} onValueChange={setTokenType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="wajebaat">Wajebaat Takhmeen</SelectItem>
                  <SelectItem value="fmb">FMB Takhmeen</SelectItem>
                  <SelectItem value="payment">Payment Token</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* VIP Option */}
            <div className="flex items-center gap-2">
              <Checkbox 
                id="vip-check"
                checked={isVip}
                onCheckedChange={setIsVip}
              />
              <Label htmlFor="vip-check" className="text-sm">
                VIP Token (1-10)
                {availableVip.length > 0 && (
                  <span className="text-xs text-slate-500 ml-2">
                    Available: {availableVip.slice(0, 5).join(', ')}{availableVip.length > 5 ? '...' : ''}
                  </span>
                )}
              </Label>
            </div>

            {/* Generate Button */}
            <Button 
              onClick={handleGenerateToken} 
              disabled={generating}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              {generating ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Ticket className="w-4 h-4 mr-2" />
              )}
              Generate Token
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Generated Token Preview */}
      {generatedToken && (
        <Card className="border-emerald-500 border-2">
          <CardHeader className="pb-2 bg-emerald-50">
            <CardTitle className="text-sm font-medium text-emerald-700 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Token Generated Successfully!
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-4xl font-bold text-navy">#{generatedToken.token_number}</p>
              <p className="text-sm text-slate-500 mt-1">{generatedToken.full_name}</p>
              <p className="text-xs text-slate-400">{generatedToken.its_id}</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Tokens List View Component
function TokensListView({ 
  tokens, 
  loading, 
  filters, 
  setFilters, 
  page, 
  setPage, 
  totalPages,
  selectedTokens,
  setSelectedTokens,
  onUpdateStatus,
  onBulkStatus,
  onSendReminder,
  onRefresh,
  showStatusFilter,
  api,
  eventId,
  onPrintTokenList,
  onBluetoothPrint,
  bluetoothConnecting,
  bluetoothPrinting,
  printerConnected,
  onConnectPrinter,
  selectedDate,
  showBluetoothPrint = false
}) {
  const handleSelectAll = () => {
    if (selectedTokens.length === tokens.length) {
      setSelectedTokens([]);
    } else {
      setSelectedTokens(tokens.map(t => t.id));
    }
  };

  const handleSelectToken = (tokenId) => {
    if (selectedTokens.includes(tokenId)) {
      setSelectedTokens(selectedTokens.filter(id => id !== tokenId));
    } else {
      setSelectedTokens([...selectedTokens, tokenId]);
    }
  };

  const handleDeleteToken = async (tokenId) => {
    if (!window.confirm('Are you sure you want to delete this token?')) return;
    
    try {
      await api.delete(`/tokens/${tokenId}`);
      toast.success('Token deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete token');
    }
  };

  const handleTakhmeenToggle = async (token) => {
    try {
      await api.put(`/tokens/${token.id}/status`, { 
        takhmeen_done: !token.takhmeen_done 
      });
      toast.success(token.takhmeen_done ? 'Takhmeen unmarked' : 'Takhmeen marked');
      onRefresh();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handlePaymentToggle = async (token) => {
    try {
      await api.put(`/tokens/${token.id}/status`, { 
        payment_done: !token.payment_done 
      });
      toast.success(token.payment_done ? 'Payment unmarked' : 'Payment marked');
      onRefresh();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters & Actions */}
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center justify-between">
        <div className="flex gap-2 flex-1">
          <Input
            placeholder="Search by name or ITS..."
            value={filters.search || ''}
            onChange={(e) => setFilters({...filters, search: e.target.value})}
            className="max-w-xs"
            data-testid="token-search-input"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={onRefresh} data-testid="refresh-tokens-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
          
          {/* Bluetooth Print - Only for Today's Tokens tab */}
          {showBluetoothPrint && (
            <>
              {!printerConnected ? (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={onConnectPrinter}
                  disabled={bluetoothConnecting}
                  data-testid="connect-bluetooth-btn"
                  className="text-blue-600 border-blue-300"
                >
                  {bluetoothConnecting ? (
                    <RefreshCw className="w-4 h-4 animate-spin mr-1" />
                  ) : (
                    <Share2 className="w-4 h-4 mr-1" />
                  )}
                  {bluetoothConnecting ? 'Connecting...' : 'Connect Printer'}
                </Button>
              ) : (
                <Button 
                  size="sm" 
                  onClick={onBluetoothPrint}
                  disabled={bluetoothPrinting || tokens.length === 0}
                  data-testid="bluetooth-print-btn"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {bluetoothPrinting ? (
                    <RefreshCw className="w-4 h-4 animate-spin mr-1" />
                  ) : (
                    <Printer className="w-4 h-4 mr-1" />
                  )}
                  {bluetoothPrinting ? 'Printing...' : `Print ${tokens.length} Tokens`}
                </Button>
              )}
            </>
          )}
          
          {/* Standard Browser Print - Only for Full Token List tab */}
          {!showBluetoothPrint && onPrintTokenList && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onPrintTokenList}
              data-testid="print-token-list-btn"
            >
              <Printer className="w-4 h-4 mr-1" />
              Print
            </Button>
          )}
        </div>
      </div>

      {/* Tokens Table */}
      {loading ? (
        <div className="flex justify-center py-8">
          <RefreshCw className="w-6 h-6 animate-spin text-navy" />
        </div>
      ) : tokens.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <Ticket className="w-12 h-12 mx-auto text-slate-300 mb-4" />
          <p>{selectedDate ? `No tokens found for ${selectedDate}` : 'No tokens found'}</p>
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox 
                      checked={selectedTokens.length === tokens.length && tokens.length > 0}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Token #</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>ITS</TableHead>
                  <TableHead>Mohalla</TableHead>
                  <TableHead>Takhmeen</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tokens.map((token) => (
                  <TableRow key={token.id}>
                    <TableCell>
                      <Checkbox 
                        checked={selectedTokens.includes(token.id)}
                        onCheckedChange={() => handleSelectToken(token.id)}
                      />
                    </TableCell>
                    <TableCell className="font-bold">#{token.token_number}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {{"wajebaat": "Waj", "fmb": "FMB", "payment": "Pay"}[token.token_type] || token.token_type}
                      </Badge>
                    </TableCell>
                    <TableCell>{token.full_name}</TableCell>
                    <TableCell className="font-mono text-sm">{token.its_id}</TableCell>
                    <TableCell>{token.mohalla}</TableCell>
                    <TableCell>
                      <Checkbox 
                        checked={token.takhmeen_done}
                        onCheckedChange={() => handleTakhmeenToggle(token)}
                      />
                    </TableCell>
                    <TableCell>
                      <Checkbox 
                        checked={token.payment_done}
                        disabled={!token.takhmeen_done}
                        onCheckedChange={() => handlePaymentToggle(token)}
                      />
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleDeleteToken(token.id)} className="text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete Token
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-2">
            {tokens.map((token) => (
              <Card key={token.id} className="p-3">
                <div className="flex items-start gap-3">
                  <Checkbox 
                    checked={selectedTokens.includes(token.id)}
                    onCheckedChange={() => handleSelectToken(token.id)}
                  />
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="font-bold text-lg">#{token.token_number}</span>
                        <Badge variant="outline" className="ml-2">
                          {{"wajebaat": "Waj", "fmb": "FMB", "payment": "Pay"}[token.token_type]}
                        </Badge>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleDeleteToken(token.id)} className="text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <p className="font-medium">{token.full_name}</p>
                    <p className="text-sm text-slate-500">{token.its_id} | {token.mohalla}</p>
                    <div className="flex gap-4 mt-2">
                      <label className="flex items-center gap-1 text-sm">
                        <Checkbox 
                          checked={token.takhmeen_done}
                          onCheckedChange={() => handleTakhmeenToggle(token)}
                        />
                        T
                      </label>
                      <label className="flex items-center gap-1 text-sm">
                        <Checkbox 
                          checked={token.payment_done}
                          disabled={!token.takhmeen_done}
                          onCheckedChange={() => handlePaymentToggle(token)}
                        />
                        P
                      </label>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-4">
              <Button 
                variant="outline" 
                size="sm" 
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <span className="py-2 px-3 text-sm">
                Page {page} of {totalPages}
              </span>
              <Button 
                variant="outline" 
                size="sm" 
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Pending HOF View Component
function PendingHofView({ eventId, api, onGenerateToken }) {
  const [pendingHofs, setPendingHofs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ mohalla: '', search: '' });

  const fetchPendingHofs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.mohalla) params.append('mohalla', filters.mohalla);
      if (filters.search) params.append('search', filters.search);
      
      const response = await api.get(`/token-events/${eventId}/pending-hof?${params}`);
      setPendingHofs(response.data.pending_hofs || []);
    } catch (error) {
      toast.error('Failed to load pending HOFs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingHofs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, filters.mohalla]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPendingHofs();
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-2">
        <Input
          placeholder="Search by name or ITS..."
          value={filters.search}
          onChange={(e) => setFilters({...filters, search: e.target.value})}
          className="max-w-xs"
        />
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex justify-center py-8">
          <RefreshCw className="w-6 h-6 animate-spin text-navy" />
        </div>
      ) : pendingHofs.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <CheckCircle className="w-12 h-12 mx-auto text-emerald-300 mb-4" />
          <p>All HOFs have generated tokens!</p>
        </div>
      ) : (
        <div className="grid gap-2">
          {pendingHofs.map((hof) => (
            <Card key={hof.its_id} className="p-3">
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium">{hof.full_name}</p>
                  <p className="text-sm text-slate-500">{hof.its_id} | {hof.mohalla}</p>
                </div>
                <Button 
                  size="sm" 
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => onGenerateToken(hof)}
                >
                  <Ticket className="w-4 h-4 mr-1" />
                  Generate
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// Payment Pending View Component
function PaymentPendingView({ eventId, api, onSuccess }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ mohalla: '', search: '' });
  const [generating, setGenerating] = useState(null);

  const fetchPaymentPending = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.mohalla) params.append('mohalla', filters.mohalla);
      if (filters.search) params.append('search', filters.search);
      
      const response = await api.get(`/token-events/${eventId}/payment-pending?${params}`);
      setMembers(response.data.members || []);
    } catch (error) {
      toast.error('Failed to load payment pending');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, filters.mohalla]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPaymentPending();
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);

  const handleGeneratePaymentToken = async (member, isVip = false) => {
    setGenerating(member.its_id);
    try {
      await api.post(`/token-events/${eventId}/generate`, {
        its_id: member.its_id,
        token_type: 'payment',
        category: isVip ? 'vip' : 'general'
      });
      toast.success('Payment token generated!');
      fetchPaymentPending();
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate token');
    } finally {
      setGenerating(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-2">
        <Input
          placeholder="Search by name or ITS..."
          value={filters.search}
          onChange={(e) => setFilters({...filters, search: e.target.value})}
          className="max-w-xs"
        />
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex justify-center py-8">
          <RefreshCw className="w-6 h-6 animate-spin text-navy" />
        </div>
      ) : members.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <CheckCircle className="w-12 h-12 mx-auto text-emerald-300 mb-4" />
          <p>No payment pending!</p>
        </div>
      ) : (
        <div className="grid gap-2">
          {members.map((member) => (
            <Card key={member.its_id} className="p-3">
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium">{member.full_name}</p>
                  <p className="text-sm text-slate-500">{member.its_id} | {member.mohalla}</p>
                  {member.has_payment_token_today && (
                    <Badge className="bg-blue-500 mt-1">Done for Today</Badge>
                  )}
                </div>
                {!member.has_payment_token_today && (
                  <Button 
                    size="sm" 
                    className="bg-purple-600 hover:bg-purple-700"
                    disabled={generating === member.its_id}
                    onClick={() => handleGeneratePaymentToken(member)}
                  >
                    {generating === member.its_id ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-1" />
                        Pay Token
                      </>
                    )}
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// Reminder Dialog Component (placeholder)
function ReminderDialog({ api, tokenIds, onClose, onSuccess }) {
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send Reminder</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500">
          Send reminder to {tokenIds.length} selected tokens
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { toast.info('Reminder feature coming soon'); onClose(); }}>Send</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
